import { notifications } from "@mantine/notifications";
import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useGetAllCategoriesQuery } from "Services/Admin/CategoryAdminApiSlice";
import { useGetAllLocTypesQuery } from "Services/Admin/LocTypeApiSlice";
import { useGetAllAdminProductsQuery } from "Services/Admin/ProductAdminApiSlice";
import { CategoryPromotionDet } from "Types/Admin/AdminCategoryPromotionType";
import { ProductMetaDTO } from "Types/ProductMetaDTO";
import { useLiveTime } from "hooks/useLiveTime";
import useDebounce from "hooks/useDebounce";
import { parsePromotionDateTime as parsePromotionDateTimeHelper } from "Utilities/PromotionDateUtils";

const PRODUCT_PAGE_SIZE = 100;

enum InternalPromotionType {
  BY_VALUE = "BY_VALUE",
  BY_QUANTITY = "BY_QUANTITY",
}

interface CategoryPromotionFormProps {
  initialValues: {
    categoryPromotionHdr: {
      byValue: number;
      crAt: string;
      crBy: string;
      customerTypeId: string;
      endDate: string;
      endTime: string;
      id: number;
      isActive: string;
      notes: string;
      outlet: string;
      plant: string;
      promotionDesc: string;
      promotionName: string;
      startDate: string;
      startTime: string;
      upAt: string;
      upBy: string;
    };
    categoryPromotionDet: {
      buyProductClassId: string;
      buyQty: number;
      crAt: string;
      crBy: string;
      getItem: string;
      getQty: number;
      hdrId: number;
      id: number;
      limitOfUsage: number;
      lnNo: number;
      plant: string;
      promotion: number;
      promotionType: string;
      upAt: string;
      upBy: string;
      usageUsed: number;
    }[];
  };
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isViewMode?: boolean;
  loading?: boolean;
}

export const CategoryPromotionForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isViewMode = false,
    loading = false,
}: CategoryPromotionFormProps) => {
  const [formData, setFormData] = useState(initialValues);
  const now = useLiveTime();

  const end = parsePromotionDateTimeHelper(
    formData.categoryPromotionHdr.endDate,
    formData.categoryPromotionHdr.endTime
  );
  const isExpired = end ? now > end : false;

  useEffect(() => {
    if (isExpired && formData.categoryPromotionHdr.isActive === "Y") {
      setFormData((prev: any) => ({
        ...prev,
        categoryPromotionHdr: {
          ...prev.categoryPromotionHdr,
          isActive: "N",
        },
      }));
    }
  }, [isExpired, formData.categoryPromotionHdr.isActive]);

  
  const [selectedBuyCategory, setSelectedBuyCategory] = useState(null);
  const [selectedGetProduct, setSelectedGetProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !isViewMode && !!formData.categoryPromotionHdr.id;
  const isFieldLocked = isViewMode || isEditMode;

const finalLoading = loading || isSubmitting;
  const plant = process.env.REACT_APP_PLANT;

  const { data: locTypes = [], isLoading } = useGetAllLocTypesQuery({
    plant: plant,
  });

  const [isByValue, setIsByValue] = useState<boolean>(
    initialValues.categoryPromotionHdr.byValue === 1,
  );

  const { data: categories = [], isLoading: categoriesLoading } =
    useGetAllCategoriesQuery();

  const [productSearchInput, setProductSearchInput] = useState("");
  const debouncedProductSearch = useDebounce(productSearchInput.trim(), 400);
  const [productPage, setProductPage] = useState(1);
  const [accumulatedProducts, setAccumulatedProducts] = useState<
    ProductMetaDTO[]
  >([]);
  const lastAppliedPageRef = useRef<string | null>(null);
  const seenOptionsRef = useRef<Map<string, { value: string; label: string }>>(
    new Map(),
  );

  useEffect(() => {
    setProductPage(1);
    setAccumulatedProducts([]);
    lastAppliedPageRef.current = null;
  }, [debouncedProductSearch]);

  const { data: productsData, isFetching: productsLoading } =
    useGetAllAdminProductsQuery(
      {
        category: undefined,
        pageSize: PRODUCT_PAGE_SIZE,
        activePage: productPage,
        subCategory: undefined,
        brand: undefined,
        search: debouncedProductSearch || undefined,
      },
      { refetchOnMountOrArgChange: true },
    );

  useEffect(() => {
    if (!productsData) return;

    const pageKey = `${debouncedProductSearch}:${productPage}`;
    if (lastAppliedPageRef.current === pageKey) return;
    lastAppliedPageRef.current = pageKey;

    setAccumulatedProducts((prev) =>
      productPage === 1 ? productsData.products : [...prev, ...productsData.products],
    );
  }, [productsData, productPage, debouncedProductSearch]);

  const categoryOptions = categories.map((category) => ({
    value: category.categoryCode,
    label: category.categoryName,
  }));

  const productOptions = accumulatedProducts.map((product) => {
    const option = {
      value: product.product.item,
      label: `${product.product.item} - ${product.product.itemDesc}`,
    };
    seenOptionsRef.current.set(option.value, option);
    return option;
  });

  const hasMoreProducts =
    (productsData?.totalProducts ?? 0) > accumulatedProducts.length;

  const handleProductMenuScrollToBottom = () => {
    if (productsLoading || !hasMoreProducts) return;
    setProductPage((prev) => prev + 1);
  };

  const resolveSelectedProductOption = (itemValue: string | undefined) => {
    if (!itemValue) return null;
    const fromCurrentList = productOptions.find(
      (option) => option.value === itemValue,
    );
    if (fromCurrentList) return fromCurrentList;

    const fromCache = seenOptionsRef.current.get(itemValue);
    if (fromCache) return fromCache;

    return { value: itemValue, label: itemValue };
  };

  const getBuyCategoryOptionsForRow = (rowIndex: number) => {
    const currentRowBuyCategory =
      formData.categoryPromotionDet[rowIndex]?.buyProductClassId;

    const selectedBuyCategories = formData.categoryPromotionDet
      .map((item) => item.buyProductClassId)
      .filter((item) => item && item.trim() !== "");

    return categoryOptions.filter((option) => {
      if (option.value === currentRowBuyCategory) return true;

      const isSelectedInOtherRows = selectedBuyCategories.some(
        (item, index) => index !== rowIndex && item === option.value,
      );

      return !isSelectedInOtherRows;
    });
  };

  const handleBuyCategoryChange = (selectedOption: any, index: number) => {
    setSelectedBuyCategory(selectedOption);
    setFormData((prev: any) => ({
      ...prev,
      categoryPromotionDet: prev.categoryPromotionDet.map(
        (item: any, i: any) =>
          i === index
            ? { ...item, buyProductClassId: selectedOption.value }
            : item,
      ),
    }));
  };

  const handleGetProductChange = (selectedOption: any, index: number) => {
    setSelectedGetProduct(selectedOption);
    setFormData((prev: any) => ({
      ...prev,
      categoryPromotionDet: prev.categoryPromotionDet.map(
        (item: any, i: any) =>
          i === index ? { ...item, getItem: selectedOption.value } : item,
      ),
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    if (name.startsWith("categoryPromotionHdr.")) {
      const field = name.split(".")[1];
      setFormData((prev: any) => ({
        ...prev,
        categoryPromotionHdr: {
          ...prev.categoryPromotionHdr,
          [field]: type === "number" ? Number(value) : value,
        },
      }));
    } else if (name.startsWith("categoryPromotionDet.")) {
      const [_, index, field] = name.split(".");
      setFormData((prev: any) => ({
        ...prev,
        categoryPromotionDet: prev.categoryPromotionDet.map(
          (item: any, i: any) =>
            i === parseInt(index)
              ? { ...item, [field]: type === "number" ? Number(value) : value }
              : item,
        ),
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: type === "number" ? Number(value) : value,
      }));
    }
  };

  const handleInternalPromotionType = (
    internalPromotionType: InternalPromotionType,
  ) => {
    const newIsByValue =
      internalPromotionType === InternalPromotionType.BY_VALUE;
    setIsByValue(newIsByValue);
    setFormData((prev: any) => ({
      ...prev,
      categoryPromotionHdr: {
        ...prev.categoryPromotionHdr,
        byValue: newIsByValue ? 1 : 0,
      },
    }));
  };

  const formatDateForInput = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return "";

    const [day, month, year] = dateStr.split("/");
    const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
      2,
      "0",
    )}`;
    return `${formattedDate}T${timeStr}:00`;
  };

  const parsePromotionDateTime = (date: string, time: string) => {
    if (!date || !time) return null;

    const dateParts = date.split("/");
    if (dateParts.length !== 3) return null;

    const [day, month, year] = dateParts;
    const [hours, minutes] = time.split(":");

    const parsedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
    );

    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const handleDateTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "start" | "end",
  ) => {
    const value = e.target.value;
    if (!value) return;

    const [datePart, timePart] = value.split("T");
    const [year, month, day] = datePart.split("-");
    const time = timePart.substring(0, 5);

    setFormData((prev: any) => ({
      ...prev,
      categoryPromotionHdr: {
        ...prev.categoryPromotionHdr,
        [`${type}Date`]: `${day}/${month}/${year}`,
        [`${type}Time`]: time,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (finalLoading) return;

    const startDateValue = parsePromotionDateTime(
      formData.categoryPromotionHdr.startDate,
      formData.categoryPromotionHdr.startTime,
    );

    const endDateValue = parsePromotionDateTime(
      formData.categoryPromotionHdr.endDate,
      formData.categoryPromotionHdr.endTime,
    );

    if (!startDateValue || !endDateValue) {
      return;
    }

    if (endDateValue < startDateValue) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allUsageExhausted =
    formData.categoryPromotionDet.length > 0 &&
    formData.categoryPromotionDet.some((det) => (det.limitOfUsage ?? 0) > 0) &&
    formData.categoryPromotionDet.every(
      (det) => (det.limitOfUsage ?? 0) <= 0 || (det.usageUsed ?? 0) >= (det.limitOfUsage ?? 0),
    );

  return (
    <form
      onSubmit={handleSubmit}
      className="font-body tracking-wider space-y-4"
    >
      <div className="bg-white px-6 py-12 rounded-md shadow-md my-6">
        {allUsageExhausted && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-4 text-sm text-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-5 w-5 shrink-0 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">Promotion fully claimed.</p>
              <p className="mt-1 text-red-700">All available redemptions for this promotion have been used up. Customers can no longer apply this offer.</p>
            </div>
          </div>
        )}
        {formData.categoryPromotionHdr.isActive === "N" && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-100 px-4 py-4 text-sm text-yellow-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">This promotion is currently inactive.</p>
              <p className="mt-1 text-yellow-700">It may have expired or been manually disabled. To reactivate it, update the end date to a future date and time, then set the status back to <strong>Active</strong>.</p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Promotion Name:*
            </label>
            <input
              type="text"
              name="categoryPromotionHdr.promotionName"
              value={formData.categoryPromotionHdr.promotionName}
              onChange={handleChange}
              readOnly={isFieldLocked}
              disabled={isFieldLocked}
              className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              required
              maxLength={100}
              placeholder="Enter promotion name (max 100 chars)"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.categoryPromotionHdr.promotionName.length}/100
              characters
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Promotion Description:*
            </label>
            <textarea
              name="categoryPromotionHdr.promotionDesc"
              value={formData.categoryPromotionHdr.promotionDesc}
              onChange={handleChange}
              readOnly={isFieldLocked}
              disabled={isFieldLocked}
              className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              maxLength={200}
              placeholder="Enter promotion description (max 200 chars)"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.categoryPromotionHdr.promotionDesc.length}/200
              characters
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Outlet/Location:*
            </label>
            <select
              name="categoryPromotionHdr.outlet"
              value={formData.categoryPromotionHdr.outlet}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              required
              disabled={isFieldLocked || isLoading}
            >
              <option value="">Select a location</option>
              {locTypes.map((locType) => (
                <option key={locType.locTypeId} value={locType.locTypeId}>
                  {locType.locTypeDesc}
                </option>
              ))}
            </select>
          </div>

          <div className="px-5 pt-8">
            <label>
              <input
                onClick={() =>
                  handleInternalPromotionType(InternalPromotionType.BY_QUANTITY)
                }
                type="radio"
                name="type"
                className="mr-1 disabled:opacity-50 disabled:cursor-not-allowed"
                checked={formData.categoryPromotionHdr.byValue === 0}
                readOnly={isFieldLocked}
                disabled={isFieldLocked}
              />
              By Quantity
            </label>
            <label className="px-2">
              <input
                onClick={() =>
                  handleInternalPromotionType(InternalPromotionType.BY_VALUE)
                }
                type="radio"
                name="type"
                className="mr-1 disabled:opacity-50 disabled:cursor-not-allowed"
                checked={formData.categoryPromotionHdr.byValue === 1}
                readOnly={isFieldLocked}
                disabled={isFieldLocked}
              />
              By Value
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Start Date & Time *
            </label>
            <input
              type="datetime-local"
              value={formatDateForInput(
                formData.categoryPromotionHdr.startDate,
                formData.categoryPromotionHdr.startTime,
              )}
              onChange={(e) => handleDateTimeChange(e, "start")}
              className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              required
              readOnly={isViewMode}
              disabled={isViewMode}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              End Date & Time *
            </label>
            <input
              type="datetime-local"
              value={formatDateForInput(
                formData.categoryPromotionHdr.endDate,
                formData.categoryPromotionHdr.endTime,
              )}
              onChange={(e) => handleDateTimeChange(e, "end")}
              className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              required
              readOnly={isViewMode}
              disabled={isViewMode}
            />
          </div>

          <div className="pt-2 px-2">
            <label className="px-2">
              <input
                type="radio"
                name="categoryPromotionHdr.isActive"
                value="Y"
                checked={formData.categoryPromotionHdr.isActive === "Y"}
                onChange={handleChange}
                className="mr-1 disabled:opacity-50 disabled:cursor-not-allowed"
                readOnly={isViewMode}
                disabled={isViewMode}
              />
              Active
            </label>
            <label className="px-2">
              <input
                type="radio"
                name="categoryPromotionHdr.isActive"
                value="N"
                checked={formData.categoryPromotionHdr.isActive === "N"}
                onChange={handleChange}
                className="mr-1 disabled:opacity-50 disabled:cursor-not-allowed"
                readOnly={isViewMode}
                disabled={isViewMode}
              />
              Non Active
            </label>
          </div>
        </div>

        <div className="overflow-auto pt-5">
          {!isByValue && (
            <table className="min-w-full border border-gray-200 text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 font-semibold">
                <tr>
                  <th className="px-4 py-2 border">Buy Category</th>
                  <th className="px-4 py-2 border">Buy Quantity</th>
                  <th className="px-4 py-2 border">Get Product</th>
                  <th className="px-4 py-2 border">Get Quantity</th>
                  <th className="px-4 py-2 border">Limit of Usage</th>
                  <th className="px-4 py-2 border">Usage Used</th>
                </tr>
              </thead>
              <tbody>
                {formData.categoryPromotionDet.map(
                  (item: CategoryPromotionDet, index: any) => (
                    <tr key={index} className="bg-white">
                      <td className="px-4 py-2 border flex items-center gap-2">
                        <Select
                          options={getBuyCategoryOptionsForRow(index)}
                          onChange={(newValue) =>
                            handleBuyCategoryChange(newValue, index)
                          }
                          value={categoryOptions.find(
                            (opt) =>
                              opt.value === item.buyProductClassId?.toString(),
                          )}
                          placeholder="Select a Category..."
                          isDisabled={isFieldLocked}
                          isLoading={categoriesLoading}
                          className="w-full basic-single"
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          components={{ IndicatorsContainer: () => null }}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              border: "1px solid #d1d5db",
                              borderRadius: "0.375rem",
                              minHeight: "auto",
                              padding: "0.25rem",
                              cursor: state.isDisabled ? "not-allowed" : "pointer",
                              backgroundColor: state.isDisabled ? "#f3f4f6" : base.backgroundColor,
                              "&:hover": { borderColor: "#d1d5db" },
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                            dropdownIndicator: () => ({ display: "none" }),
                            indicatorSeparator: () => ({ display: "none" }),
                          }}
                        />
                      </td>
                      <td className="px-4 py-2 border">
                        <input
                          type="number"
                          min="1"
                          name={`categoryPromotionDet.${index}.buyQty`}
                          value={item.buyQty || ""}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.value = target.value.replace(/^0+/, "");
                          }}
                          onChange={handleChange}
                          className="border px-2 py-1 rounded w-full text-right disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          readOnly={isFieldLocked}
                          disabled={isFieldLocked}
                          maxLength={8}
                        />
                      </td>
                      <td className="px-4 py-2 border flex items-center gap-2">
                        <Select
                          options={productOptions}
                          onChange={(newValue) =>
                            handleGetProductChange(newValue, index)
                          }
                          value={resolveSelectedProductOption(
                            item.getItem?.toString(),
                          )}
                          placeholder="Select a Product..."
                          isDisabled={isFieldLocked}
                          isLoading={productsLoading}
                          onMenuScrollToBottom={handleProductMenuScrollToBottom}
                          onInputChange={(newValue, actionMeta) => {
                            if (actionMeta.action === "input-change") {
                              setProductSearchInput(newValue);
                            }
                          }}
                          filterOption={() => true}
                          noOptionsMessage={() =>
                            productsLoading ? "Loading..." : "No products found"
                          }
                          className="w-full basic-single"
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          components={{ IndicatorsContainer: () => null }}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              border: "1px solid #d1d5db",
                              borderRadius: "0.375rem",
                              minHeight: "auto",
                              padding: "0.25rem",
                              cursor: state.isDisabled ? "not-allowed" : "pointer",
                              backgroundColor: state.isDisabled ? "#f3f4f6" : base.backgroundColor,
                              "&:hover": { borderColor: "#d1d5db" },
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                            dropdownIndicator: () => ({ display: "none" }),
                            indicatorSeparator: () => ({ display: "none" }),
                          }}
                        />
                      </td>
                      <td className="px-4 py-2 border">
                        <input
                          type="number"
                          min="1"
                          name={`categoryPromotionDet.${index}.getQty`}
                          value={item.getQty || ""}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.value = target.value.replace(/^0+/, "");
                          }}
                          onChange={handleChange}
                          className="border px-2 py-1 rounded w-full text-right disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          readOnly={isFieldLocked}
                          disabled={isFieldLocked}
                          maxLength={8}
                        />
                      </td>
                      <td className="px-4 py-2 border">
                        <input
                          type="number"
                          min="1"
                          name={`categoryPromotionDet.${index}.limitOfUsage`}
                          value={item.limitOfUsage || ""}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.value = target.value.replace(/^0+/, "");
                          }}
                          onChange={handleChange}
                          className="border px-2 py-1 rounded w-full text-right disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          readOnly={isViewMode}
                          disabled={isViewMode}
                          maxLength={8}
                        />
                      </td>
                      <td className="px-4 py-2 border">
                        <div className="border px-2 py-1 rounded w-full text-right bg-gray-50 text-gray-600 select-none">
                          {item.usageUsed ?? 0}
                        </div>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          )}

          {isByValue && (
            <table className="min-w-full border border-gray-200 text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 font-semibold">
                <tr>
                  <th className="px-4 py-2 border">Buy Category</th>
                  <th className="px-4 py-2 border">Buy Quantity</th>
                  <th className="px-4 py-2 border">Type</th>
                  <th className="px-4 py-2 border">Promotion Value</th>
                  <th className="px-4 py-2 border">Limit of Usage</th>
                  <th className="px-4 py-2 border">Usage Used</th>
                </tr>
              </thead>
              <tbody>
                {formData.categoryPromotionDet.map((item: any, index: any) => (
                  <tr key={index}>
                    <td className="border px-4 py-2">
                      <Select
                        options={getBuyCategoryOptionsForRow(index)}
                        onChange={(newValue) =>
                          handleBuyCategoryChange(newValue, index)
                        }
                        value={categoryOptions.find(
                          (opt) =>
                            opt.value === item.buyProductClassId?.toString(),
                        )}
                        placeholder="Select a Category..."
                        isDisabled={isFieldLocked}
                        isLoading={categoriesLoading}
                        className="w-full basic-single"
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        components={{ IndicatorsContainer: () => null }}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            border: "1px solid #d1d5db",
                            borderRadius: "0.375rem",
                            minHeight: "auto",
                            padding: "0.25rem",
                            cursor: state.isDisabled ? "not-allowed" : "pointer",
                            backgroundColor: state.isDisabled ? "#f3f4f6" : base.backgroundColor,
                            "&:hover": { borderColor: "#d1d5db" },
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                          dropdownIndicator: () => ({ display: "none" }),
                          indicatorSeparator: () => ({ display: "none" }),
                        }}
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <input
                        type="number"
                        min="1"
                        name={`categoryPromotionDet.${index}.buyQty`}
                        value={item.buyQty || ""}
                        onChange={handleChange}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.value = target.value.replace(/^0+/, "");
                        }}
                        className="w-full border rounded px-2 py-1 text-right disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        readOnly={isFieldLocked}
                        disabled={isFieldLocked}
                        maxLength={8}
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <select
                        name={`categoryPromotionDet.${index}.promotionType`}
                        value={item.promotionType}
                        onChange={handleChange}
                        className="w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        disabled={isFieldLocked}
                      >
                        <option value="%">Percentage</option>
                        <option value="INR">Fixed Amount</option>
                      </select>
                    </td>
                    <td className="border px-4 py-2">
                      <input
                        type="number"
                        min="1"
                        name={`categoryPromotionDet.${index}.promotion`}
                        value={item.promotion || ""}
                        onChange={handleChange}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.value = target.value.replace(/^0+/, "");
                        }}
                        className="w-full border rounded px-2 py-1 text-right disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        readOnly={isFieldLocked}
                        disabled={isFieldLocked}
                        maxLength={8}
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <input
                        type="number"
                        min="1"
                        name={`categoryPromotionDet.${index}.limitOfUsage`}
                        value={item.limitOfUsage || ""}
                        onChange={handleChange}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.value = target.value.replace(/^0+/, "");
                        }}
                        className="w-full border rounded px-2 py-1 text-right disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        readOnly={isViewMode}
                        disabled={isViewMode}
                        maxLength={8}
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <div className="border px-2 py-1 rounded w-full text-right bg-gray-50 text-gray-600 select-none">
                        {item.usageUsed ?? 0}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-blue-600 pt-3 px-2">
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:text-gray-400"
            onClick={() => {
              setFormData((prev: any) => ({
                ...prev,
                categoryPromotionDet: [
                  ...prev.categoryPromotionDet,
                  {
                    buyProductClassId: "",
                    buyQty: 0,
                    crAt: "",
                    crBy: "",
                    getItem: "",
                    getQty: 0,
                    hdrId: 0,
                    id: 0,
                    limitOfUsage: 0,
                    lnNo: prev.categoryPromotionDet.length + 1,
                    plant: formData.categoryPromotionHdr.plant,
                    promotion: 0,
                    promotionType: "%",
                    upAt: "",
                    upBy: "",
                    usageUsed: 0,
                  },
                ],
              }));
            }}
            disabled={isFieldLocked}
          >
            + Add Another Line
          </button>
        </div>

        <div className="pt-4">
          <label className="block text-sm font-medium text-gray-500 mb-1">
            Notes
          </label>
          <textarea
            name="categoryPromotionHdr.notes"
            value={formData.categoryPromotionHdr.notes}
            onChange={handleChange}
            readOnly={isFieldLocked}
            disabled={isFieldLocked}
            className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            maxLength={200}
            placeholder="Enter notes (max 200 chars)"
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {formData.categoryPromotionHdr.notes.length}/200 characters
          </div>
        </div>

        <hr className="border-gray-300 my-8" />

        <div className="flex items-center justify-start gap-3 mt-4">
  <button
    className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50"
    type="button"
    onClick={onCancel}
    disabled={finalLoading}
  >
    Cancel
  </button>

  {!isViewMode && (
    <button
      className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
      type="submit"
      disabled={finalLoading}
    >
      {finalLoading && (
        <svg
          className="animate-spin h-4 w-4 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {finalLoading ? "Saving..." : "Save"}
    </button>
  )}
</div>
      </div>
    </form>
  );
};
